<template>
    <div>

        <Card :header="$t('productsRanking')" overlay :loading="request.getLoading()">

            <ErrorMessage :value="request.getError()" />

            <template #nav>
                <div class="d-flex gap-3 flex-column">
                    <div class="d-flex gap-3">
                        <b-dropdown :text="$t(`${currentChart}`)" toggle-class="caret wide">
                            <template v-for="chartType in ['impressions', 'quantity', 'clicks', 'amount']">
                                <b-dropdown-item :key="chartType" :active="chartType === currentChart" @click="() => currentChart = chartType">{{ $t(`${chartType}`) }}</b-dropdown-item>
                            </template>
                        </b-dropdown>
                        <Multiselect
                            :fetch-options-function="fetchCampaigns"
                            :placeholder="$t('selectCampaign')"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="request.getLoading()"
                            show-checkboxes
                            show-selected
                            multiple
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @inputTrackBy="campaigns = $event"
                            @change="loadData()"
                        />
                        <Multiselect
                            v-show="showProductGroupsResults"
                            :fetch-options-function="fetchSpaceGroups"
                            :placeholder="$t('productGroup')"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="request.getLoading()"
                            show-checkboxes
                            show-selected
                            multiple
                            preselect-all
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @inputTrackBy="groups = $event"
                            @change="loadData()"
                        />
                        <FormSelectTree
                            v-show="!showProductGroupsResults"
                            ref="FormTreeSelect"
                            v-model="categories"
                            :disabled="request.getLoading()"
                            :load-options="fetchCategories"
                            :normalizer="normalizer"
                            :placeholder="$t('category')"
                            multiple
                            class="width-200"
                            show-count-value
                            @change="loadData()"
                        />
                    </div>
                    <div class="d-flex gap-3 justify-content-end align-items-center">
                        <b-form-checkbox v-model="showProductGroupsResults" switch>{{ $t('showResultsForProductGroups') }}</b-form-checkbox>
                    </div>
                </div>
            </template>

            <Chart
                :series="[]"
                :options="chartOptions"
            />

        </Card>

    </div>
</template>



<script>
    import Multiselect from '@/components/common/Multiselect.vue';
    import FormSelectTree from '@/components/common/FormSelectTree.vue';
    import Chart from '@/components/common/Chart.vue';
    import { calcPercentDiff } from '@/utils/number';

    export default {
        name: 'ProductRanking',
        components: {
            Multiselect,
            FormSelectTree,
            Chart,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                requestImpressions: this.$req('space/ImpressionsRanking'),
                requestClicks: this.$req('space/ClicksRanking'),
                requestQuantity: this.$req('space/QuantityRanking'),
                requestAmount: this.$req('space/AmountRanking'),
                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                requestProductsTree: this.$req('space/ProductsTree'),
                requestSpaceGroups: this.$req('producerProductGroups/SpaceGroups'),
                currentChart: 'quantity',
                campaigns: [],
                categories: [],
                groups: [],
                showProductGroupsResults: false,
            };
        },
        computed: {
            request() {
                if (this.currentChart === 'impressions') return this.requestImpressions;
                if (this.currentChart === 'clicks') return this.requestClicks;
                if (this.currentChart === 'quantity') return this.requestQuantity;
                if (this.currentChart === 'amount') return this.requestAmount;
                return null;
            },
            data() {
                return this.request.getData() || [];
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const requestParams = { ...(this.timeRange || {}) };
                if (this.campaigns?.length) requestParams.campaigns = this.campaigns.join(',');
                if (!this.showProductGroupsResults && this.categories?.length) requestParams.categories = this.categories.join(',');
                if (this.showProductGroupsResults && this.groups?.length) requestParams.groups = this.groups.join(',');
                return { urlParams, requestParams };
            },
            chartSeries() {
                return [{ name: this.$t(`${this.currentChart}`), data: this.data.map(item => item.summary) }];
            },
            pointsAnnotations() {
                const chartSeries = this.data.map(item => item.summary);
                const categories = this.data.map(item => item.name);
                const additionalData = this.data.map(item => item.summaryComparison);
                const pointsAnnotations = [];
                for (let i = 0; i < chartSeries.length; i++) {
                    const value = additionalData[i];
                    const diff = calcPercentDiff(chartSeries[i], value);
                    const diffPercent = this.$format.percent(diff);
                    const color = diff > 0 ? 'green' : 'red';
                    const triangleSymbol = diff > 0 ? '▲' : '▼';
                    if (chartSeries[i] !== 0 || value !== 0) {
                        pointsAnnotations.push({
                            x: chartSeries[i],
                            y: categories[i],
                            marker: {
                                fillColor: '#fff',
                                strokeColor: color,
                                radius: 2,
                            },
                            label: {
                                text: `${triangleSymbol} ${diffPercent}`,
                                borderColor: color,
                                style: {
                                    color: '#fff',
                                    background: color,
                                },
                                offsetX: 0,
                                offsetY: 0,
                            },
                        });
                    }
                }
                return pointsAnnotations;
            },
            chartOptions() {
                const length = this.data.length;
                return {
                    series: this.chartSeries,
                    chart: {
                        type: 'bar',
                        height: length ? length * 30 + 70 : 300,
                    },
                    plotOptions: {
                        bar: {
                            horizontal: true,
                        },
                    },
                    xaxis: {
                        type: 'category',
                        categories: this.data.map(item => item.name),
                        tickAmount: 10,
                    },
                    dataLabels: {
                        enabled: true,
                    },
                    annotations: {
                        points: this.pointsAnnotations,
                    },
                };
            },
        },
        watch: {
            spaceId: {
                handler: 'loadData',
            },
            timeRange: {
                handler: 'loadData',
            },
            currentChart: {
                handler: 'loadData',
            },
            showProductGroupsResults: {
                handler: 'loadData',
            },
        },
        methods: {
            loadData() {
                this.fetchRanking();
            },
            fetchRanking() {
                this.request.get(this.apiParams);
            },
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id, v: 1 } });
            },
            fetchProductsTree(categoryId) {
                return this.requestProductsTree.get({ urlParams: { spaceId: this.spaceId }, requestParams: { category: categoryId } });
            },
            fetchSpaceGroups() {
                return this.requestSpaceGroups.get({ urlParams: { spaceId: this.spaceId } }).then(data => data.items);
            },
            fetchCategories({ parentNode }) {
                return this.fetchProductsTree(parentNode?.entityId);
            },
            normalizer(node) {
                return {
                    id: node.entityId,
                    label: node.text,
                    children: node.children === true ? null : node.children,
                    isDisabled: node.type === 'product',
                };
            },
        },
        created() {
            this.loadData();
        },
    };
</script>



<style lang="scss" scoped></style>
